import React, { useState } from 'react';
import { addJobComment, setJobFieldConditions } from '../services/api';
import MentionTextarea from './MentionTextarea';

// Shown at the end of the stop-timer flow. The crew MUST answer before it closes —
// there's no cancel/skip and the button stays disabled until everything is filled in.
//
// Two jobs: the note the office reads, and — only when the crew says field work is
// finished — the site observations that feed the field-hours estimating model.
//
// The field questions are asked ONLY after field work. Stopping a drafting or office
// timer used to ask "field work finished?" anyway, with no way to skip it; answering
// "yes" wrote a work_finished_date and site observations from a day nobody was on
// site. Non-field stops now ask for the note and nothing else.
//
// Every question asks what they SAW, never how hard it felt. "Was this a tough one?"
// would measure self-justification: a crew that burned 40 hours has every reason to
// say yes. Counts and conditions can't be argued with.
//
// The scales are frozen. Changing the wording or the number of options makes every
// answer recorded before the change non-comparable — add a new question instead.

const MONUMENT_OPTIONS = [
  { value: 'most', label: 'Found most' },
  { value: 'some', label: 'Found some' },
  { value: 'none', label: 'Almost none — rebuilt from deeds' },
];

const CONDITION_OPTIONS = [
  { value: 'open', label: 'Open — clear shots' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'heavy', label: 'Heavy — cutting line / no GPS' },
];

const CORNERS_MIN = 0;
const CORNERS_MAX = 500;

// The work types that put someone on the property. Must match the gate the field
// departure checklist uses in JobTimer.js and TimeTracking.js.
export const FIELD_WORK_TYPES = ['Field Work', 'Final Mark Up / Stake Lines'];

function Label({ children }) {
  return (
    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '18px 0 8px' }}>
      {children}
    </div>
  );
}

// Stacked full-width buttons rather than a <select>: one tap instead of
// tap-scroll-tap, and every option stays visible so nobody picks blind.
function OptionRow({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            style={{
              width: '100%', minHeight: 48, padding: '12px 14px', textAlign: 'left',
              background: active ? '#16a34a' : '#fff',
              color: active ? '#fff' : '#111827',
              border: `2px solid ${active ? '#16a34a' : '#d1d5db'}`,
              borderRadius: 10, fontSize: 15, fontWeight: active ? 700 : 500,
              fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function RequiredCommentModal({ jobId, jobNumber, workType, onDone }) {
  // Unknown work type is treated as field work — the old behaviour. Better to ask a
  // question that might not apply than to silently stop collecting the site data.
  const isFieldWork = workType == null || FIELD_WORK_TYPES.includes(workType);
  const [text, setText] = useState('');
  const [finished, setFinished] = useState(null);   // null until answered
  const [corners, setCorners] = useState('');
  const [monument, setMonument] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const clearError = () => setError('');

  const bumpCorners = (delta) => {
    // Decrementing an EMPTY field used to write "0" (Math.max(0, -1) === 0), which
    // satisfied validation — so a single minus tap was the fastest way past the
    // question, and it looked like a real answer. Three of the first four close-outs
    // came back 0. A typed 0 is still allowed; an accidental one is not.
    if (corners === '' && delta < 0) return;
    const current = corners === '' ? 0 : parseInt(corners, 10) || 0;
    const next = Math.min(CORNERS_MAX, Math.max(CORNERS_MIN, current + delta));
    setCorners(String(next));
    clearError();
  };

  const cornersValid = corners !== '' && !Number.isNaN(parseInt(corners, 10));
  const conditionsComplete = cornersValid && monument !== null && conditions !== null;
  // Office work owes a note and nothing more.
  const canSubmit = isFieldWork
    ? (!!text.trim() && finished !== null && (!finished || conditionsComplete))
    : !!text.trim();

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true); setError('');
    try {
      // Conditions FIRST, deliberately. It's an idempotent update, so a retry after a
      // half-failure just rewrites the same values — whereas the note is append-only
      // and retrying it would post a duplicate. This order makes the whole thing safe
      // to retry from any failure point.
      if (isFieldWork && finished) {
        await setJobFieldConditions(jobId, {
          corners_count: parseInt(corners, 10),
          monument_recovery: monument,
          site_conditions: conditions,
          field_work_complete: true,
        });
      }
      await addJobComment(jobId, text.trim());
      onDone();
    } catch (e) {
      setError('Could not save. Check your connection and try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100002, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, maxWidth: 420, width: '100%', maxHeight: 'calc(90dvh - 60px)', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Wrapping up {jobNumber}</h3>
        <p style={{ color: '#4b5563', margin: '0 0 4px', fontSize: 14 }}>
          {isFieldWork
            ? 'A couple of quick questions before you close out.'
            : 'Leave a note before you close out.'}
        </p>

        {isFieldWork && (
        <>
        <Label>Field work finished on this job?</Label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ v: false, l: 'No — going back' }, { v: true, l: "Yes — we're done" }].map(({ v, l }) => {
            const active = finished === v;
            return (
              <button
                key={String(v)}
                type="button"
                disabled={saving}
                onClick={() => { setFinished(v); clearError(); }}
                style={{
                  flex: 1, minHeight: 52, padding: '12px 10px',
                  background: active ? '#16a34a' : '#fff',
                  color: active ? '#fff' : '#111827',
                  border: `2px solid ${active ? '#16a34a' : '#d1d5db'}`,
                  borderRadius: 10, fontSize: 15, fontWeight: active ? 700 : 500,
                  fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {l}
              </button>
            );
          })}
        </div>

        {/* Only asked once, on the visit the crew calls it done — so the answers
            describe the finished job rather than a half-done one. */}
        {finished === true && (
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 18, paddingTop: 2 }}>
            <Label>Corners set or recovered</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button" disabled={saving} onClick={() => bumpCorners(-1)}
                style={{ width: 52, height: 52, flexShrink: 0, fontSize: 24, fontWeight: 700, background: '#f3f4f6', color: '#111827', border: '2px solid #d1d5db', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                −
              </button>
              <input
                value={corners}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, '');
                  setCorners(v === '' ? '' : String(Math.min(CORNERS_MAX, parseInt(v, 10))));
                  clearError();
                }}
                disabled={saving}
                inputMode="numeric"
                placeholder="—"
                style={{ flex: 1, minWidth: 0, height: 52, boxSizing: 'border-box', textAlign: 'center', fontSize: 22, fontWeight: 700, fontFamily: 'inherit', border: '2px solid #d1d5db', borderRadius: 10, color: '#111827' }}
              />
              <button
                type="button" disabled={saving} onClick={() => bumpCorners(1)}
                style={{ width: 52, height: 52, flexShrink: 0, fontSize: 24, fontWeight: 700, background: '#f3f4f6', color: '#111827', border: '2px solid #d1d5db', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                +
              </button>
            </div>

            <Label>Existing monuments</Label>
            <OptionRow options={MONUMENT_OPTIONS} value={monument} disabled={saving}
              onChange={(v) => { setMonument(v); clearError(); }} />

            <Label>Terrain &amp; vegetation</Label>
            <OptionRow options={CONDITION_OPTIONS} value={conditions} disabled={saving}
              onChange={(v) => { setConditions(v); clearError(); }} />
          </div>
        )}
        </>
        )}

        <Label>What did you get done?</Label>
        {/* No autoFocus: on a phone it throws up the keyboard immediately and hides
            the questions above, which is how they'd get answered carelessly. */}
        <MentionTextarea
          value={text}
          onChange={(v) => { setText(v); clearError(); }}
          disabled={saving}
          rows={4}
          placeholder="What did you complete? Anything the office needs to know?"
          style={{ width: '100%', boxSizing: 'border-box', padding: 12, border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, fontFamily: 'inherit', resize: 'vertical' }}
        />

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{error}</div>}

        <button
          onClick={submit}
          disabled={saving || !canSubmit}
          style={{ width: '100%', marginTop: 14, padding: '15px', background: canSubmit ? '#16a34a' : '#d1d5db', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 16, cursor: (saving || !canSubmit) ? 'not-allowed' : 'pointer' }}
        >
          {saving ? 'Saving…' : 'Post & Finish'}
        </button>
        <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 10 }}>
          {(isFieldWork && finished === null)
            ? 'Answer the question above to close out the job.'
            : (isFieldWork && finished && !conditionsComplete)
              ? 'Answer all three questions to close out the job.'
              : 'A note is required to close out the job.'}
        </div>
      </div>
    </div>
  );
}
